/**
 * t520u: per-channel permissions. A channel can limit who may do each action to specific roles, teams, buildings and
 * people. An action with no rule keeps the current behaviour (role permissions only), so nothing changes until someone
 * customises it. Workspace owners and admins (workspace-wide "Manage channels") and the channel's owner always pass.
 * The API evaluates the rules (ChannelPermissionsService); clients only read the caller's result (`my_permissions`).
 */

export const CHANNEL_ACTIONS = ['post', 'reply', 'react', 'edit_own', 'delete_own', 'delete_any', 'pin', 'manage'] as const;
export type ChannelAction = typeof CHANNEL_ACTIONS[number];

/** 'everyone' = everyone in the channel. Roles, teams (departments and groups), buildings and people. */
export type ChannelPrincipalType = 'everyone' | 'role' | 'team' | 'location' | 'user';
export interface ChannelPrincipal { type: ChannelPrincipalType; id: string | null; name?: string | null }

/** Absent or null = the default for that action; an array = limited to those principals (empty = only the people who always can). */
export type ChannelRules = Partial<Record<ChannelAction, ChannelPrincipal[] | null>>;
export type ChannelActionSet = Record<ChannelAction, boolean>;

/** GET/PUT /conversations/:id/permissions */
export interface ChannelPermissionsView {
  rules: ChannelRules;
  /** The caller may change these (they can manage the channel). */
  can_edit: boolean;
  /** Roles to choose from (only when can_edit; the roles list itself is admin-only). */
  roles?: { id: string; name: string }[];
}

export interface ChannelActionInfo {
  key: ChannelAction;
  label: string;
  /** What the default (no rule) means, in plain words. */
  default_label: string;
  /** Moderation actions default to the channel's owners and admins rather than everyone. */
  moderation: boolean;
}

export const CHANNEL_ACTION_INFO: ChannelActionInfo[] = [
  { key: 'post', label: 'Post new messages', default_label: 'Everyone in the channel', moderation: false },
  { key: 'reply', label: 'Reply in threads', default_label: 'Everyone in the channel', moderation: false },
  { key: 'react', label: 'React', default_label: 'Everyone in the channel', moderation: false },
  { key: 'edit_own', label: 'Edit their own messages', default_label: 'Everyone in the channel', moderation: false },
  { key: 'delete_own', label: 'Delete their own messages', default_label: 'Everyone in the channel', moderation: false },
  { key: 'delete_any', label: 'Delete anyone’s messages', default_label: 'Channel owners and admins', moderation: true },
  { key: 'pin', label: 'Pin messages', default_label: 'Everyone in the channel', moderation: false },
  { key: 'manage', label: 'Manage channel settings and members', default_label: 'Channel owners and admins', moderation: true },
];

export const CHANNEL_ALWAYS_NOTE = 'Workspace owners and admins, and the channel’s owner, can always post, moderate and change these settings.';
export const CHANNEL_CANNOT_POST = 'Only some people can post here';
export const CHANNEL_CANNOT_REPLY = 'Only some people can reply here';

export const ALL_CHANNEL_ACTIONS: ChannelActionSet = Object.fromEntries(CHANNEL_ACTIONS.map(a => [a, true])) as ChannelActionSet;

export type ChannelPreset = 'open' | 'announcements' | 'selected' | 'read_only';
/**
 * The presets in "Start from". "Admins" means the people chosen for Post (nobody chosen = only workspace owners and
 * admins and the channel's owner, who can always post).
 */
export const CHANNEL_PRESETS: { key: ChannelPreset; label: string; detail: string; limited: ChannelAction[] }[] = [
  { key: 'open', label: 'Everyone can post', detail: '', limited: [] },
  { key: 'announcements', label: 'Announcements only', detail: 'admins post, others read and react', limited: ['post', 'reply'] },
  { key: 'selected', label: 'Selected people post', detail: 'everyone can reply and react', limited: ['post'] },
  { key: 'read_only', label: 'Read-only', detail: 'admins post, others only read', limited: ['post', 'reply', 'react'] },
];
/** "Announcements only: admins post, others read and react" (the preset's full name in a list). */
export const channelPresetText = (p: { label: string; detail: string }) => (p.detail ? `${p.label}: ${p.detail}` : p.label);

/** Apply a preset: the limited actions go to `who` (the people chosen to post); every other member action goes back to its default. */
export function applyChannelPreset(rules: ChannelRules, preset: ChannelPreset, who: ChannelPrincipal[]): ChannelRules {
  const p = CHANNEL_PRESETS.find(x => x.key === preset)!;
  const out: ChannelRules = { ...rules };
  for (const a of ['post', 'reply', 'react'] as ChannelAction[]) out[a] = p.limited.includes(a) ? [...who] : null;
  return out;
}

/** Which preset the rules match, if any (post/reply/react only; other actions don't affect it). */
export function channelPresetOf(rules: ChannelRules): ChannelPreset | null {
  const lim = (a: ChannelAction) => Array.isArray(rules[a]);
  const same = (a: ChannelAction) => JSON.stringify(key(rules.post)) === JSON.stringify(key(rules[a]));
  if (!lim('post') && !lim('reply') && !lim('react')) return 'open';
  if (lim('post') && !lim('reply') && !lim('react')) return 'selected';
  if (lim('post') && lim('reply') && !lim('react') && same('reply')) return 'announcements';
  if (lim('post') && lim('reply') && lim('react') && same('reply') && same('react')) return 'read_only';
  return null;
}
const key = (p: ChannelPrincipal[] | null | undefined) => (p ?? []).map(x => `${x.type}:${x.id ?? ''}`).sort();

/** Is the action customised (limited to a list)? */
export const isCustomised = (rules: ChannelRules, a: ChannelAction) => Array.isArray(rules[a]);
export const customisedActions = (rules: ChannelRules) => CHANNEL_ACTIONS.filter(a => isCustomised(rules, a));

/** "Role: Manager, Sarah Jones and 2 more" - names first, plain. */
export function principalsText(list: ChannelPrincipal[] | null | undefined, info?: ChannelActionInfo): string {
  if (!Array.isArray(list)) return info?.default_label ?? 'Everyone in the channel';
  if (list.some(p => p.type === 'everyone')) return 'Everyone in the channel';
  if (!list.length) return 'Only workspace owners and admins';
  const names = list.map(p => p.name ?? principalTypeLabel(p.type));
  return names.length <= 3 ? joinAnd(names) : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

export function principalTypeLabel(t: ChannelPrincipalType): string {
  return t === 'everyone' ? 'Everyone in the channel' : t === 'role' ? 'Role' : t === 'team' ? 'Team' : t === 'location' ? 'Building' : 'Person';
}

const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** The muted one-line summary shown while the Permissions section is collapsed. */
export function channelPermissionsSummary(rules: ChannelRules): string {
  const changed = customisedActions(rules);
  if (!changed.length) return 'Everyone in the channel can post';
  const preset = channelPresetOf(rules);
  const head = preset === 'selected' ? 'Only selected people can post'
    : preset === 'announcements' ? 'Only selected people can post or reply'
    : preset === 'read_only' ? 'Read-only: only selected people can post, reply or react'
    : isCustomised(rules, 'post') ? 'Only selected people can post' : 'Everyone in the channel can post';
  const inHead = (a: ChannelAction) => a === 'post' ? isCustomised(rules, 'post')
    : a === 'reply' ? preset === 'announcements' || preset === 'read_only'
    : a === 'react' ? preset === 'read_only' : false;
  const counted = changed.filter(a => !inHead(a));
  return counted.length ? `${head} · ${counted.length} other ${counted.length === 1 ? 'change' : 'changes'}` : head;
}

/** What the caller may do in a conversation; missing (older API, DMs) means everything the role allows, as before. */
export function myChannelActions(c: { my_permissions?: Partial<ChannelActionSet> | null }): ChannelActionSet {
  return { ...ALL_CHANNEL_ACTIONS, ...(c.my_permissions ?? {}) };
}

/** Message menu: may this person edit / delete this message? */
export function canEditMessage(c: { my_permissions?: Partial<ChannelActionSet> | null }, m: { sender_id: string | null }, me: string | null | undefined): boolean {
  return Boolean(me) && m.sender_id === me && myChannelActions(c).edit_own;
}
export function canDeleteMessage(c: { my_permissions?: Partial<ChannelActionSet> | null }, m: { sender_id: string | null }, me: string | null | undefined, moderatorFallback = false): boolean {
  const a = myChannelActions(c);
  if (Boolean(me) && m.sender_id === me) return a.delete_own;
  return c.my_permissions ? a.delete_any : moderatorFallback;
}

/** Same rules, ignoring order and names (for the settings' Save button). */
export const sameChannelRules = (a: ChannelRules, b: ChannelRules) => JSON.stringify(normRules(a)) === JSON.stringify(normRules(b));
const normRules = (r: ChannelRules) => Object.entries(r).filter(([, v]) => Array.isArray(v))
  .map(([k, v]) => [k, (v as ChannelPrincipal[]).map(p => `${p.type}:${p.id ?? ''}`).sort()] as const).sort(([a], [b]) => a.localeCompare(b));
/** Rules as sent to the API (ids only). */
export const channelRulesForSave = (r: ChannelRules): ChannelRules =>
  Object.fromEntries(Object.entries(r).map(([k, v]) => [k, Array.isArray(v) ? v.map(p => ({ type: p.type, id: p.id })) : null]));
